/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';
import { useTrackingConsent } from '@/hooks/use-tracking-consent';
import { LYTICS_HOST } from '@/lib/contentstack/constants';
import { TrackingConsent } from '@/types/tracking-consent';

export function LyticsTag({ accountId }: { accountId: string }) {
    const { trackingConsent, isTrackingConsentEnabled } = useTrackingConsent();
    const allowed = Boolean(accountId) && (!isTrackingConsentEnabled || trackingConsent === TrackingConsent.Accepted);

    useEffect(() => {
        if (!allowed || document.getElementById('lytics-jstag')) {
            return;
        }
        const script = document.createElement('script');
        script.id = 'lytics-jstag';
        script.async = true;
        script.src = `${LYTICS_HOST}/api/tag/${encodeURIComponent(accountId)}/latest.min.js`;
        document.body.appendChild(script);
    }, [accountId, allowed]);

    return null;
}
